import { Box, Button, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import { monospaceStyle } from "../../theme";
import { CapabilityPartDetail } from "../../types/capabilityPartDetail";
import { EntityRef } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import LabelValueGrid from "../Detail/LabelValueGrid";
import { codeWords } from "../Detail/format";
import { exploreAddress } from "./partExplore";

interface PartOverviewProps {
  part: CapabilityPartDetail;
}

// A caption beneath a value, in the quieter colour
const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" component="div" color="text.secondary">
    {children}
  </Typography>
);

const Count: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// "1 organization", "2 projects": a count of claims with its noun
const counted = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

// Parts as links in a line, parted by commas
const PartList: React.FC<{ parts: EntityRef[] }> = ({ parts }) => (
  <>
    {parts.map((part, index) => (
      <React.Fragment key={part.id}>
        {index > 0 && ", "}
        <EntityLink id={part.id} type={part.type} name={part.name} />
      </React.Fragment>
    ))}
  </>
);

/**
 * The overview of a part: its definition, how many products claim it in how
 * many claims, with the claims of the other bearers beneath, the part it is
 * filed under with the parts beside it there, and the step into Explore
 * with the part picked where Explore has a filter for its role.
 */
const PartOverview: React.FC<PartOverviewProps> = ({ part }) => {
  const broader = part.tree?.broader ?? null;
  const beside = part.tree?.beside ?? [];
  const { products, organizations, projects, categories } = part.bearers;

  // The claims of the bearers that are not products, by their type
  const otherBearers = [
    organizations.total > 0 &&
      counted(organizations.total, "organization", "organizations"),
    projects.total > 0 && counted(projects.total, "project", "projects"),
    categories.total > 0 && counted(categories.total, "category", "categories"),
  ]
    .filter(Boolean)
    .join(" · ");

  // Explore with the part picked lists the products the view counts. A part
  // of a role without a filter there has no such list.
  const explore = useMemo(
    () => exploreAddress([{ id: part.id, role: part.role }]),
    [part.id, part.role],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <LabelValueGrid
        rows={[
          { label: "Definition", value: part.description, wide: true },
          {
            label: "Claimed by",
            value: (
              <>
                <Count>{part.product_count}</Count>{" "}
                {part.product_count === 1 ? "product" : "products"}{" "}
                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  in <Count>{products.total}</Count>{" "}
                  {products.total === 1 ? "claim" : "claims"}
                </Box>
                {otherBearers && <Caption>{otherBearers}</Caption>}
              </>
            ),
          },
          {
            label: "Sits under",
            value: (broader || beside.length > 0) && (
              <>
                {broader && <PartList parts={[broader]} />}
                {beside.length > 0 && (
                  <Caption>
                    siblings: <PartList parts={beside} />
                  </Caption>
                )}
              </>
            ),
          },
        ]}
      />
      {explore && (
        <Button
          component={RouterLink}
          to={explore}
          variant="text"
          size="small"
          sx={{ alignSelf: "flex-start", px: 0, minWidth: 0 }}
        >
          Open in Explore with this {codeWords(part.role)} →
        </Button>
      )}
    </Box>
  );
};

export default PartOverview;
